import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Clock from "./Clock";
import useFlow from "../hooks/useFlow";
import { clearHubSpotState } from "../utils/hubspot";
import useReloadTracker from "../hooks/useReloadTracker";

export default function Layout() {
  const location = useLocation();

  const {
    selectedSiteId,
    selectedLocationId,
    showRegisterModal,
    showTermsModal,
    showMapResourceModal,
    clearClarityIdentification,
  } = useFlow();

  // Track reload status - check every 5 minutes
  const reloadTracker = useReloadTracker(5 * 60 * 1000);

  // Scroll handling
  const checkScroll = () => {
    const scrolledDown = window.scrollY > 0;
    const moreContentBelow =
      window.innerHeight + Math.ceil(window.scrollY) <
      document.documentElement.scrollHeight;
    return { scrolledDown, moreContentBelow };
  };

  const [showTopScroll, setShowTopScroll] = useState(
    checkScroll().scrolledDown,
  );
  const [showBottomScroll, setShowBottomScroll] = useState(
    checkScroll().moreContentBelow,
  );

  useEffect(() => {
    const handleScroll = () => {
      const { scrolledDown, moreContentBelow } = checkScroll();
      setShowTopScroll(scrolledDown);
      setShowBottomScroll(moreContentBelow);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const { moreContentBelow } = checkScroll();
    setShowBottomScroll(moreContentBelow);

    const observer = new MutationObserver(() => {
      const { moreContentBelow } = checkScroll();
      setShowBottomScroll(moreContentBelow);
    });

    observer.observe(document, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [location]);

  const scrollUp = () => {
    window.scrollTo({
      top: window.scrollY - window.innerHeight / 2,
      behavior: "smooth",
    });
  };

  const scrollDown = () => {
    window.scrollTo({
      top: window.scrollY + window.innerHeight / 2,
      behavior: "smooth",
    });
  };

  // Idle handling
  const timeoutIdRef = useRef(null);
  const intervalIdRef = useRef(null);
  const [withConversation, setWithConversation] = useState(false);

  const timeoutSeconds = 30;
  const timeoutShowSeconds = timeoutSeconds / 2;

  const [remainingTime, setRemainingTime] = useState(timeoutSeconds);

  useEffect(() => {
    clearHubSpotState();
  }, []);

  // Clear HubSpot when navigating to Home (main menu) - next user is likely taking over the kiosk
  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const isHomeScreen = segments.length === 2; // e.g. /30313/228
    if (isHomeScreen) {
      clearHubSpotState();
    }
  }, [location.pathname]);

  // Force reload if page hasn't reloaded for 12+ hours
  useEffect(() => {
    if (reloadTracker.shouldForceReload) {
      console.warn(
        "[ReloadTracker] Forcing reload - page hasn't reloaded for 12+ hours",
        {
          hoursSinceLastReload: reloadTracker.hoursSinceLastReload,
          status: reloadTracker.status,
        },
      );
      window.location.reload();
    }
  }, [
    reloadTracker.shouldForceReload,
    reloadTracker.hoursSinceLastReload,
    reloadTracker.status,
  ]);

  // Staff editing settings shouldn't be kicked back to the home screen
  // mid-form by the 30 second client idle timer.
  const isSettingsPage = location.pathname.endsWith("/settings");

  // Staff-only screen: no clock, and the HubSpot bubble is hidden by CSS off
  // this class rather than unloading the widget the rest of the app shares.
  useEffect(() => {
    document.body.classList.toggle("settings-screen", isSettingsPage);
    return () => document.body.classList.remove("settings-screen");
  }, [isSettingsPage]);

  useEffect(() => {
    if (
      !isSettingsPage &&
      (location.pathname.split("/").length > 3 ||
        withConversation ||
        showRegisterModal ||
        showTermsModal ||
        showMapResourceModal) &&
      import.meta.env.MODE !== "development"
    ) {
      const refreshPage = () => {
        console.log(
          "[Clarity] Idle timeout triggered - refreshing page and clearing identification",
          {
            remainingTime,
            selectedSiteId,
            selectedLocationId,
          },
        );
        clearHubSpotState();
        // Clear Clarity identification before refreshing to prevent cross-contamination
        if (clearClarityIdentification) {
          clearClarityIdentification();
        }
        window.location.href = `/${selectedSiteId}/${selectedLocationId}`;
      };

      const resetTimeout = () => {
        if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = setTimeout(refreshPage, timeoutSeconds * 1000);

        // Reset the remaining time
        setRemainingTime(timeoutSeconds);
      };
      resetTimeout();

      // Update the remaining time every second
      intervalIdRef.current = setInterval(() => {
        setRemainingTime((prevTime) => prevTime - 1);
      }, 1000);

      const handleActivity = () => {
        resetTimeout();
        if (intervalIdRef.current) clearInterval(intervalIdRef.current);
        intervalIdRef.current = setInterval(() => {
          setRemainingTime((prevTime) => prevTime - 1);
        }, 1000);
      };

      const handleHsConversationStart = () => {
        console.log("handleHsConversationStart");
        setWithConversation(true);
        resetTimeout();
      };
      const handleHsConversationClose = () => {
        console.log("handleHsConversationClose");
        setWithConversation(false);
        resetTimeout();
      };

      window.addEventListener("mousemove", handleActivity);
      window.addEventListener("keydown", handleActivity);
      window.addEventListener("scroll", handleActivity);
      if (window.HubSpotConversations) {
        window.HubSpotConversations.on(
          "conversationStarted",
          handleHsConversationStart,
        );
        window.HubSpotConversations.on(
          "conversationClosed",
          handleHsConversationClose,
        );
      }

      return () => {
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }
        window.removeEventListener("mousemove", handleActivity);
        window.removeEventListener("keydown", handleActivity);
        window.removeEventListener("scroll", handleActivity);
        if (window.HubSpotConversations) {
          window.HubSpotConversations.off(
            "conversationStarted",
            handleHsConversationStart,
          );
          window.HubSpotConversations.off(
            "conversationClosed",
            handleHsConversationClose,
          );
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLocationId,
    selectedSiteId,
    location,
    withConversation,
    showRegisterModal,
    showTermsModal,
    showMapResourceModal,
    clearClarityIdentification,
    isSettingsPage,
  ]);

  return (
    <main className="App">
      {remainingTime <= timeoutShowSeconds && (
        <div
          className={
            "timer" +
            (remainingTime <= timeoutShowSeconds - 1 ? " timerShow" : "")
          }
        >
          <p>
            Touch the screen if you need more time.
            <br />
            <br />
            Page refreshes in <b>{remainingTime}</b>
          </p>
        </div>
      )}
      {!isSettingsPage && <Clock />}
      <div className="bg-custom">
        <Outlet />
      </div>
      {showTopScroll && (
        <a
          className="scrollButton scrollUp"
          data-testid="scroll-up-btn"
          onClick={scrollUp}
        >
          <img src="/arrow_up.png" width="20" className="mx-auto" />
          Scroll
        </a>
      )}

      {showBottomScroll && (
        <a
          className="scrollButton scrollDown"
          data-testid="scroll-down-btn"
          onClick={scrollDown}
        >
          Scroll
          <img src="/arrow_down.png" width="20" className="mx-auto" />
        </a>
      )}
      <ToastContainer position="bottom-center" />
    </main>
  );
}
